import { PermissionFeatures, PermissionGroups, UserInfo, UserSettings } from '@Entities/user/user'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import {
    attemptIdvSignedIn,
    changeUserPassword,
    createUserAndAttemptIdv,
    editUserInfo,
    editUserSettings,
    enrollUser2fa,
    getUserInfo,
    submitPhoneEnrollmentMfaCode,
} from '@App/api/routes/user'
import groupData from '@App/configs/static/groups.json'
import featureData from '@App/configs/static/features.json'
import getPhoneNumberDisplay from '@App/helpers/work-entities/utils/get-phone-number-display'
import { getCountryTitle } from '@App/helpers/get-country-title'
import RootStore from '@App/stores/root-store'
import { UserRegistrationType } from '@Entities/schemas/user/user-registration'

export default class UserStore {
    static readonly internalGroupName = 'Internal User'

    private _userInfo: UserInfo

    private _au10TixWorkflowLink: string

    private rootStore: RootStore
    constructor(rootStore: RootStore) {
        makeObservable<UserStore, '_userInfo' | '_au10TixWorkflowLink'>(this, {
            _userInfo: observable,
            _au10TixWorkflowLink: observable,
            phoneNumberDisplay: computed,
            countryTitle: computed,
            isInternal: computed,
            isActionDisabled: computed,
            permissionGroupsFiltered: computed,
            address: computed,
            phoneNumber: computed,
            changeUserSetting: action,
            compactViewToggle: action,
            setAu10TixWorkflowLink: action,
        })
        this.rootStore = rootStore
    }

    get userInfo(): UserInfo {
        return this._userInfo
    }

    // TODO: These were changed to static json in MPOR-1968 - we should change to API calls w/ caching
    get permissionGroups(): PermissionGroups[] {
        return groupData
    }

    get permissionGroupsFiltered(): PermissionGroups[] {
        let filteredGroupData = groupData
        if (!this.isInternal) {
            filteredGroupData = filteredGroupData.filter((permission) => permission.id !== 'internal_user')
        }
        return filteredGroupData
    }

    /* TODO: These were changed to static json in MPOR-1968 - we should change to API calls w/ caching */
    get permissionFeatures(): PermissionFeatures[] {
        return featureData
    }

    get phoneNumberDisplay(): string {
        return getPhoneNumberDisplay(this._userInfo)
    }

    get countryTitle(): string {
        return getCountryTitle(this.rootStore.libStore.countries, this._userInfo.countryId)
    }

    get isInternal(): boolean {
        return !!(this._userInfo && this._userInfo.isInternal)
    }

    get isActionDisabled(): boolean {
        return !this.userInfo || !!this.userInfo.isInternal
    }

    get address() {
        const { streetAddress, unitNumber, city, state, countryId, postalCode } = this._userInfo

        return {
            streetAddress: streetAddress,
            unitNumber: unitNumber || '',
            city: city,
            state: state || '',
            countryId: countryId ? String(countryId) : '',
            postalCode: postalCode ? postalCode : '',
        }
    }

    get phoneNumber() {
        const { phoneCountryCode, fullPhoneNumber } = this._userInfo
        return {
            phoneCountryCode: String(phoneCountryCode ?? ''),
            fullPhoneNumber: fullPhoneNumber ?? '',
        }
    }

    public async loadUserInfo(): Promise<void> {
        const response = await getUserInfo()
        const data = response.data
        runInAction(() => (this._userInfo = data))
    }

    public async createUserAndAttemptIdv(newUser: Partial<UserRegistrationType>): Promise<boolean> {
        const response = await createUserAndAttemptIdv(newUser)
        this.setAu10TixWorkflowLink(response.data.response.securemeLink)
        return true
    }

    public async attemptIdvForSignedInUser(): Promise<boolean> {
        if (this.userInfo.exposedId) {
            const res = await attemptIdvSignedIn(this.userInfo.exposedId)
            this.setAu10TixWorkflowLink(res.data.response.securemeLink)
            return true
        } else {
            this.rootStore.popupStore.setAlertData('User ID not found.', true)
            return false
        }
    }

    public setAu10TixWorkflowLink(link: string): void {
        this._au10TixWorkflowLink = link
    }

    public compactViewToggle(newSetting: boolean): void {
        if (this.userInfo?.settings) {
            this.userInfo.settings.showDetailedWorkCards = newSetting
            editUserInfo(this.userInfo).then(() => this.loadUserInfo())
        }
    }

    public async changeUserSetting(settingToChange: keyof UserSettings, valueToChangeTo?: boolean): Promise<void> {
        const settings = this.userInfo?.settings
        if (!settings) {
            return
        }

        settings[settingToChange] = valueToChangeTo !== undefined ? valueToChangeTo : !settings[settingToChange]

        try {
            await editUserSettings(settings)
        } catch (error) {
            this.rootStore.popupStore.setAlertFromError(error)
        }
    }

    public async submitPhoneEnrollmentMfaCode(code: string): Promise<any> {
        return submitPhoneEnrollmentMfaCode(code)
    }

    public async enrollUser2fa(
        useSms: boolean,
        email: string,
        password: string,
        phoneCountryCode: string,
        fullPhoneNumber: string
    ): Promise<string> {
        const result = await enrollUser2fa(useSms, email, password, phoneCountryCode, fullPhoneNumber)
        return result.data.mfa
    }

    public changeUserPassword(code: string, proposedPassword: string): Promise<any> {
        return changeUserPassword(code, proposedPassword)
    }

    public getAu10TixWorkflowLinkParams(): string {
        const urlObj = new URL(this._au10TixWorkflowLink)
        const searchParams = new URLSearchParams(urlObj.search)

        return searchParams.toString()
    }
}
